import React, { useState, useEffect } from 'react';
import { Form, StandardZipInput } from 'reaxl-forms';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Button, Link, Select, Image } from 'reaxl';
import { useMakeDropdown, useModelDropdown, useZipcodeInput, useSearchSubmit, useModelOptions } from '@/contexts/VehicleSearchContext';
import { paymentsDuck } from '@/ducks';
import { callToActionAnalytics } from '@/analytics';
import BrowseByStylesModal from './BrowseByStylesModal';
import { handleSearchByBudgetLinkClick } from './BrowseByLinks';
import { defaultBudgetPrice } from '@/constants';

const HeadingChallengerA = 'Cars for Sale - Search Near You';
const HeadingChallengerB = 'Choose your car-buying adventure.';

function BrowseByLinksRefresh({
    dispatch,
    paymentsInfo,
    analyticsPageType,
    isMobile,
    device,
    zipCode,
    dealerId,
    styleCodes,
    isClient,
}) {
    const [showStyles, setShowStyles] = useState(false);
    const isMobilePortrait = isMobile && device.orientation === 'portrait';

    const separtor = (
        <div className="text-size-200 text-size-sm-200 display-inline-block padding-horizontal-0 padding-horizontal-sm-1">
            &nbsp;|&nbsp;
        </div>
    );

    const wrapperClassName = 'text-size-200 text-size-sm-200 text-link padding-0';

    const toggleStylesModal = (e) => {
        if (!showStyles) {
            callToActionAnalytics.fireBrowseByStyle(e, {
                pageType: analyticsPageType,
                zip: zipCode,
                dealerId,
            });
        }
        setShowStyles(!showStyles);
    };

    const handleAdvancedSearchLinkClick = (e) => {
        callToActionAnalytics.fireAdvancedSearchLink(e, {
            pageType: analyticsPageType,
        });
    };

    return (
        <>
            <Button
                className={wrapperClassName}
                bsStyle="text"
                data-qaid="lnk-browseByBudget"
                title="What Can I Afford?"
                role="button"
                onClick={(e) => handleSearchByBudgetLinkClick(
                    e,
                    analyticsPageType,
                    dispatch,
                    paymentsInfo,
                )}
            >
                What Can I Afford?
            </Button>
            {separtor}
            <Button
                className={wrapperClassName}
                bsStyle="text"
                data-qaid="lnk-browseByStyle"
                title="Shop Used Cars"
                role="button"
                onClick={toggleStylesModal}
            >
                Shop Used Cars
            </Button>
            {isClient && isMobilePortrait ? <br /> : separtor}
            <Link
                className={wrapperClassName}
                undecorated
                role="link"
                href="/cars-for-sale/advanced-search"
                onClick={handleAdvancedSearchLinkClick}
            >
                Advanced Search
            </Link>
            <BrowseByStylesModal
                showModal={showStyles}
                showHideModalToggleCallback={toggleStylesModal}
                styleCodes={styleCodes}
                listingType="used"
            />
        </>
    );
}

function SearchBox({
    isMobile,
    device,
    dealerId,
    styleCodes,
    enableRefreshedHeroVariantA,
    enableRefreshedHeroVariantB,
    makeConfig,
    modelConfig,
    labelClassName,
    ctaText,
}) {
    const [isClient, setIsClient] = useState(false);

    const heading = enableRefreshedHeroVariantA
        ? HeadingChallengerA
        : HeadingChallengerB;

    const dispatch = useDispatch();
    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo) || {};
    const analyticsPageType = 'homepage';

    // To fix hydration errors
    useEffect(() => {
        setIsClient(true);
    }, []);

    const { make, makeOptions, handleMakeChange } = useMakeDropdown();
    const { model, handleModelChange } = useModelDropdown();
    const { zipcode, handleZipcodeChange } = useZipcodeInput();
    const { handleSearchSubmit } = useSearchSubmit();
    const modelOptions = useModelOptions();

    return (
        <>
            <div className="search-box-container">
                <div className="search-box-width display-flex flex-column">
                    <div
                        data-cmp="SearchBox"
                        className="search-box colored-background inverted bg-primary"
                    >
                        <h1 className="heading">{heading}</h1>
                        <div>
                            <Form
                                onSubmit={handleSearchSubmit}
                                role="form"
                            >
                                <div className="col-xs-12 input-container padding-horizontal-0">
                                    <div className="col-xs-6 padding-left-0">
                                        <Select
                                            aria-label={`Select ${makeOptions[0]}`}
                                            name="makeCode"
                                            value={make.value}
                                            options={makeOptions}
                                            help={makeConfig?.help || ''}
                                            onChange={(e) => handleMakeChange(e, makeConfig?.analyticsData)}
                                            labelClassName={labelClassName}
                                        />
                                    </div>
                                    <div className="col-xs-6 padding-right-0">
                                        <Select
                                            aria-label={`Select ${modelOptions[0]}`}
                                            name="ModelCode"
                                            value={model.value}
                                            options={modelOptions}
                                            help={modelConfig?.help || ''}
                                            onChange={(e) => handleModelChange(e, modelConfig?.analyticsData)}
                                            labelClassName={labelClassName}
                                        />
                                    </div>
                                    <div className="col-xs-6 padding-left-0">
                                        <StandardZipInput
                                            aria-label="Select ZIP Code"
                                            name="zipcode"
                                            placeholder="ZIP Code"
                                            value={zipcode}
                                            onChange={handleZipcodeChange}
                                            required
                                            labelClassName={labelClassName}
                                        />
                                    </div>
                                    <div className="col-xs-6 padding-right-0">
                                        <Button
                                            block
                                            id="search"
                                            bsStyle="primary-inverse"
                                            type="submit"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        </div>
                        <div className="links-container">
                            <BrowseByLinksRefresh
                                dispatch={dispatch}
                                paymentsInfo={paymentsInfo}
                                analyticsPageType={analyticsPageType}
                                isMobile={isMobile}
                                device={device}
                                zipCode={zipcode}
                                dealerId={dealerId}
                                styleCodes={styleCodes}
                                isClient={isClient}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="hero-bar">
                <div className="hero-bar-width">
                    <div className="details-container">
                        <Text
                            color="subdued"
                            weight="bold"
                        >
                            Kelley Blue Book® My Wallet &nbsp;
                        </Text>
                        {isClient && isMobile && <br />}
                        <Text>
                            Set a budget and get real payments on every car.
                        </Text>
                        <Button
                            className="col-xs-12 padding-left-0"
                            bsStyle="text"
                            data-qaid="lnk-browseByBudget"
                            title="Shop Cars for Sale"
                            onClick={(e) => {
                                const { budget } = paymentsInfo;

                                if (budget === null || budget === 0 || budget === '0') {
                                    dispatch(paymentsDuck?.creators?.updatePaymentsInfo({ budget: defaultBudgetPrice }));
                                }
                                dispatch(paymentsDuck?.creators?.updateMyWallet({ displayModal: true, selectedPanelName: 'budget' }));
                                callToActionAnalytics.fireSetMyBudgetClick(e, { text: ctaText, withPixallData: true });
                            }}
                        >
                            Shop Cars for Sale
                        </Button>
                    </div>
                    <div className="sponsor-container">
                        <Text>Sponsored by</Text>
                        <Image
                            src="//www.autotrader.com/content/dam/autotrader/homepage/kbb/KBB.com_Lockup_Color_RGB_Small.png"
                            alt="KBB logo"
                            lazyload={false}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default SearchBox;
